import type { MessageKey } from '@/shared/i18n/messages';

/**
 * ThemeColors — a tenant's palette for the whole site.
 *
 * This generalises what used to be `Grid/colors.ts`: twelve hex values that
 * repainted spreadsheet cells and nothing else. The mechanism was already the
 * right one — override the semantic tokens of `src/styles/tokens.css` by
 * injecting a `<style>` at a higher specificity — so it is kept verbatim and
 * only the surface it covers grows.
 *
 * Three decisions shape everything below.
 *
 * 1. **A theme is a PARTIAL record.** Only the roles an organization actually
 *    chose are stored and injected; everything else keeps resolving from
 *    `tokens.css`, which means it keeps following the brand (Numtable indigo /
 *    Procalendar emerald) and the theme for free. An all-or-nothing record
 *    would have frozen every Procalendar tenant onto the Numtable accent the
 *    moment they picked a background colour.
 *
 * 2. **One picker can drive several tokens.** `--surface-2`, `--accent-hover`,
 *    `--faint` and friends are relationships, not independent choices, so they
 *    are derived with `color-mix` from the role the user did pick. Forty-odd
 *    swatches is already a lot of screen; a hundred would be unusable and would
 *    let a tenant break their own contrast in ways no warning could catch.
 *
 * 3. **Dark mode is selected by `data-theme`, not a `.dark` class**, and the
 *    injected CSS also covers `prefers-color-scheme` so that the `system`
 *    setting behaves. Carried over from the grid implementation, where getting
 *    this wrong meant the dark palette silently never appeared.
 */

/* ------------------------------------------------------------------ roles --
   The unit the user edits. `base` is the storage key without its mode prefix;
   the stored record keys are `light_<base>` and `dark_<base>`. */

export const THEME_COLOR_GROUPS = ['chrome', 'document', 'status'] as const;
export type ThemeColorGroup = (typeof THEME_COLOR_GROUPS)[number];

export interface ThemeColorRole {
  readonly base: string;
  readonly group: ThemeColorGroup;
  /** i18n key, never a literal — the settings screen translates it. */
  readonly labelKey: MessageKey;
  /**
   * The CSS custom properties this role writes, given the chosen hex. Anything
   * omitted keeps resolving from tokens.css. `mix` builds the `color-mix()`
   * expressions; the surrounding block always defines `--text` and `--surface`
   * before they are referenced, or leaves them to the tokens.
   */
  readonly vars: (hex: string) => Readonly<Record<string, string>>;
}

/** `color-mix(in srgb, <a> <pct>%, <b>)` — every derivation goes through this. */
function mix(a: string, pct: number, b: string): string {
  return `color-mix(in srgb, ${a} ${pct}%, ${b})`;
}

export const THEME_COLOR_ROLES: readonly ThemeColorRole[] = [
  /* ------------------------------------------------------------- chrome -- */
  {
    base: 'topbar',
    group: 'chrome',
    labelKey: 'settings.theme.role.topbar',
    vars: (c) => ({ '--header-bg': c }),
  },
  {
    base: 'topbar_text',
    group: 'chrome',
    labelKey: 'settings.theme.role.topbarText',
    // The muted/hover/border trio in tokens.css is the header foreground at
    // reduced alpha, so it stays legible on whatever the top bar became.
    vars: (c) => ({
      '--header-fg': c,
      '--header-muted': mix(c, 75, 'transparent'),
      '--header-hover': mix(c, 14, 'transparent'),
      '--header-border': mix(c, 22, 'transparent'),
    }),
  },
  {
    base: 'background',
    group: 'chrome',
    labelKey: 'settings.theme.role.background',
    // surface-2 (cards) and surface-3 (hover, inset) are the page pushed a few
    // percent towards the text colour, which works in both directions: on a
    // dark background the derived steps get lighter on their own.
    vars: (c) => ({
      '--surface': c,
      '--surface-2': mix('var(--text)', 4, c),
      '--surface-3': mix('var(--text)', 8, c),
    }),
  },
  {
    base: 'border',
    group: 'chrome',
    labelKey: 'settings.theme.role.border',
    vars: (c) => ({
      '--border': c,
      '--border-strong': mix(c, 70, 'var(--text)'),
    }),
  },
  {
    base: 'highlight',
    group: 'chrome',
    labelKey: 'settings.theme.role.highlight',
    // `--ring`, `--focus` and the whole `--brand*` family are aliases of
    // `--accent` in tokens.css, so they follow without being named here.
    vars: (c) => ({
      '--accent': c,
      '--accent-hover': mix(c, 80, 'var(--text)'),
      '--accent-soft': mix(c, 10, 'var(--surface)'),
      '--accent-fg': readableOn(c),
      '--grid-selection': c,
      '--grid-hover': mix(c, 10, 'var(--surface)'),
    }),
  },
  {
    base: 'text',
    group: 'chrome',
    labelKey: 'settings.theme.role.text',
    vars: (c) => ({ '--text': c }),
  },
  {
    base: 'muted',
    group: 'chrome',
    labelKey: 'settings.theme.role.muted',
    vars: (c) => ({
      '--muted': c,
      '--faint': mix(c, 70, 'var(--surface)'),
    }),
  },
  {
    base: 'link',
    group: 'chrome',
    labelKey: 'settings.theme.role.link',
    vars: (c) => ({
      '--link': c,
      '--link-hover': mix(c, 80, 'var(--text)'),
    }),
  },

  /* ----------------------------------------------------------- document -- */
  {
    base: 'sheet_name_text',
    group: 'document',
    labelKey: 'settings.theme.role.sheetName',
    vars: (c) => ({ '--sheet-name-text': c }),
  },
  {
    base: 'header_fill',
    group: 'document',
    labelKey: 'settings.theme.role.headerFill',
    vars: (c) => ({ '--grid-head': c }),
  },
  {
    base: 'header_text',
    group: 'document',
    labelKey: 'settings.theme.role.headerText',
    vars: (c) => ({ '--grid-head-text': c }),
  },
  {
    base: 'group_fill',
    group: 'document',
    labelKey: 'settings.theme.role.groupFill',
    vars: (c) => ({ '--grid-group-bg': c }),
  },
  {
    base: 'group_text',
    group: 'document',
    labelKey: 'settings.theme.role.groupText',
    vars: (c) => ({ '--grid-group-text': c }),
  },
  {
    base: 'cell_edit_pale',
    group: 'document',
    labelKey: 'settings.theme.role.editPale',
    vars: (c) => ({ '--cell-edit-pale': c }),
  },
  {
    base: 'cell_edit_deep',
    group: 'document',
    labelKey: 'settings.theme.role.editDeep',
    vars: (c) => ({ '--cell-edit-deep': c }),
  },
  {
    base: 'cell_readonly_pale',
    group: 'document',
    labelKey: 'settings.theme.role.readOnlyPale',
    vars: (c) => ({ '--cell-ro-pale': c }),
  },
  {
    base: 'cell_readonly_deep',
    group: 'document',
    labelKey: 'settings.theme.role.readOnlyDeep',
    vars: (c) => ({ '--cell-ro-deep': c }),
  },
  {
    base: 'chip',
    group: 'document',
    labelKey: 'settings.theme.role.chip',
    vars: (c) => ({ '--chip-bg': c }),
  },
  {
    base: 'grid_line',
    group: 'document',
    labelKey: 'settings.theme.role.gridLine',
    vars: (c) => ({
      '--grid-line': c,
      '--grid-line-strong': mix(c, 60, 'var(--text)'),
    }),
  },

  /* ------------------------------------------------------------- status -- */
  {
    base: 'success',
    group: 'status',
    labelKey: 'settings.theme.role.success',
    vars: (c) => ({ '--success': c }),
  },
  {
    base: 'danger',
    group: 'status',
    labelKey: 'settings.theme.role.danger',
    vars: (c) => ({ '--danger': c }),
  },
  {
    base: 'warning',
    group: 'status',
    labelKey: 'settings.theme.role.warning',
    vars: (c) => ({ '--warning': c }),
  },
];

/**
 * The two painted modes. Deliberately NOT `ThemeMode` from `applyTheme.ts`,
 * which is the user's preference and includes `system`; a palette has a value
 * for light and a value for dark, and `system` picks between them at runtime.
 */
export type ThemeColorMode = 'light' | 'dark';

export const THEME_COLOR_KEYS: readonly string[] = THEME_COLOR_ROLES.flatMap((role) => [
  `light_${role.base}`,
  `dark_${role.base}`,
]);

const KEY_SET = new Set(THEME_COLOR_KEYS);

/** A tenant's palette. Partial on purpose — see the header. */
export type ThemeColors = Readonly<Record<string, string>>;

export function themeKey(mode: ThemeColorMode, base: string): string {
  return `${mode}_${base}`;
}

/* --------------------------------------------------------------- colour -- */

export function isHexColor(value: unknown): value is string {
  return typeof value === 'string' && /^#[0-9a-f]{6}$/i.test(value);
}

/** Every entry is a known key holding a `#rrggbb`. Unknown keys are dropped. */
export function isThemeColors(value: unknown): value is ThemeColors {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  return Object.entries(value as Record<string, unknown>).every(
    ([key, hex]) => KEY_SET.has(key) && isHexColor(hex),
  );
}

/** Keeps the known `#rrggbb` entries and discards anything else. */
export function sanitizeThemeColors(value: unknown): ThemeColors {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  const out: Record<string, string> = {};
  for (const [key, hex] of Object.entries(value as Record<string, unknown>)) {
    if (KEY_SET.has(key) && isHexColor(hex)) out[key] = hex;
  }
  return out;
}

export function parseHex(hex: string): readonly [number, number, number] {
  const n = Number.parseInt(hex.slice(1), 16);
  return [(n >> 16) & 0xff, (n >> 8) & 0xff, n & 0xff];
}

/** WCAG relative luminance. */
export function luminance(hex: string): number {
  const channel = (v: number): number => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  const [r, g, b] = parseHex(hex);
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

/** WCAG contrast ratio between two opaque colours, 1–21. */
export function contrastRatio(a: string, b: string): number {
  const la = luminance(a);
  const lb = luminance(b);
  const [hi, lo] = la > lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
}

/** Black or white, whichever is legible on `hex`. Used for `--accent-fg`. */
export function readableOn(hex: string): string {
  return contrastRatio(hex, '#ffffff') >= contrastRatio(hex, '#000000')
    ? '#ffffff'
    : '#000000';
}

/* ------------------------------------------------------------ injection -- */

const STYLE_ELEMENT_ID = 'numtable-theme-colors';

/** The declarations for one mode, or `''` when that mode chose nothing. */
function declarations(colors: ThemeColors, mode: ThemeColorMode): string {
  const out: string[] = [];
  for (const role of THEME_COLOR_ROLES) {
    const hex = colors[themeKey(mode, role.base)];
    if (!isHexColor(hex)) continue;
    for (const [property, value] of Object.entries(role.vars(hex))) {
      out.push(`${property}:${value};`);
    }
  }
  return out.join('');
}

/**
 * Override the site's CSS variables by injecting a `<style>`, or remove the
 * override entirely when passed `null` so the tokens take back over. Returns an
 * undo closure that restores whatever was injected before, which is what lets a
 * document's palette shadow the organization's and be put back on unmount.
 *
 * The selectors mirror how the theme is chosen elsewhere: the explicit
 * `data-theme` in both directions wins over `prefers-color-scheme`, which is
 * what makes the `system` setting behave. `:root:root` is a specificity bump
 * over tokens.css, not a typo.
 *
 * Both the media blocks and the `[data-theme]` blocks are emitted, and each
 * media block is guarded with `:not([data-theme="<the other one>"])`. A partial
 * record is why: if a tenant sets only dark roles, there is no light block to
 * out-specify the media one, and a viewer whose OS is dark but who chose Light
 * would get the dark palette. The guard settles it in the selector rather than
 * relying on a block that may not exist.
 */
export function applyThemeColors(colors: ThemeColors | null): () => void {
  const existing = document.getElementById(STYLE_ELEMENT_ID);
  const previousText = existing?.textContent ?? null;
  const previousExists = Boolean(existing);

  const restore = (): void => {
    const current = document.getElementById(STYLE_ELEMENT_ID);
    if (!previousExists) {
      current?.remove();
      return;
    }
    const restored = current ?? document.createElement('style');
    restored.id = STYLE_ELEMENT_ID;
    restored.textContent = previousText ?? '';
    if (!restored.parentNode) document.head.appendChild(restored);
  };

  const light = colors ? declarations(colors, 'light') : '';
  const dark = colors ? declarations(colors, 'dark') : '';

  if (!light && !dark) {
    existing?.remove();
    return restore;
  }

  // Every block is CONDITIONAL, including the light one. The grid palette this
  // replaced could emit `:root:root{…light…}` unconditionally because it always
  // wrote all twelve values in both modes, so the dark block always covered it.
  // A ThemeColors record is partial: a tenant may set a light background and
  // leave dark alone, and an unconditional light block would then paint dark
  // mode with the light background — the token's own dark value never gets a
  // chance, because nothing in the dark block mentions it.
  const css =
    (light
      ? `@media (prefers-color-scheme: light){:root:root:not([data-theme="dark"]){${light}}}`
      : '') +
    (dark
      ? `@media (prefers-color-scheme: dark){:root:root:not([data-theme="light"]){${dark}}}`
      : '') +
    (light ? `:root:root[data-theme="light"]{${light}}` : '') +
    (dark ? `:root:root[data-theme="dark"]{${dark}}` : '');

  const element = (existing as HTMLStyleElement | null) ?? document.createElement('style');
  if (!existing) {
    element.id = STYLE_ELEMENT_ID;
    document.head.appendChild(element);
  }
  element.textContent = css;
  return restore;
}

/* -------------------------------------------------------------- legacy --- */

/**
 * The six grid roles the old `grid_colors` setting stored, per mode, mapped
 * onto their ThemeColors equivalents.
 */
const LEGACY_BASE: Readonly<Record<string, string>> = {
  edit_pale: 'cell_edit_pale',
  edit_deep: 'cell_edit_deep',
  readonly_pale: 'cell_readonly_pale',
  readonly_deep: 'cell_readonly_deep',
  header: 'header_fill',
  chip: 'chip',
};

/**
 * Read a stored `grid_colors` value as a ThemeColors.
 *
 * The old envelope was `{ custom: {...} }`, `{ presetKey: '...' }` or the bare
 * record, and only twelve keys ever existed. Everything else stays unset, which
 * is precisely right: a tenant who had customised their cell bands wanted their
 * cell bands changed and the rest of the site left alone.
 */
export function upgradeGridColors(value: unknown): ThemeColors {
  const unwrapped =
    value && typeof value === 'object' && 'custom' in value
      ? (value as { custom: unknown }).custom
      : value;
  if (!unwrapped || typeof unwrapped !== 'object' || Array.isArray(unwrapped)) return {};

  const out: Record<string, string> = {};
  for (const [key, hex] of Object.entries(unwrapped as Record<string, unknown>)) {
    if (!isHexColor(hex)) continue;
    const mode = key.startsWith('light_') ? 'light' : key.startsWith('dark_') ? 'dark' : null;
    if (!mode) continue;
    const base = LEGACY_BASE[key.slice(mode.length + 1)];
    if (base) out[themeKey(mode, base)] = hex;
  }
  return out;
}

/* ------------------------------------------------------------- presets --- */

export interface ThemePreset {
  /** i18n key, never a literal. */
  readonly nameKey: MessageKey;
  readonly colors: ThemeColors;
}

/**
 * Named palettes. These are *data*, the same way a theme is: the only hex in
 * the module outside the defaults table, and they never apply unless chosen.
 * "No preset" is not listed — it means following the tokens, which is what an
 * empty record already does.
 */
export const THEME_PRESETS: readonly ThemePreset[] = [
  {
    /**
     * A neutral surface in both modes, with the brand left alone.
     *
     * Every value is Grey's, minus four roles: `topbar`, `topbar_text`,
     * `highlight` and `link`. That omission is the whole point of the preset
     * and the only thing separating it from Grey. Those four carry brand
     * identity — `link` defaults to the accent, so it belongs with them — and
     * leaving them unset means they keep resolving from `tokens.css`. A
     * Procalendar tenant picking Classic keeps their emerald top bar and
     * emerald links; a Numtable one keeps indigo. Grey, by contrast, paints
     * those grey too, and that is the choice a tenant makes by picking Grey.
     *
     * It used to be indigo-tinted in both modes. Those exact values are still
     * here as `Indigo` below, which also keeps every existing tenant matching a
     * named preset rather than "Custom".
     */
    nameKey: 'settings.theme.preset.classic',
    colors: {
      light_background: '#fbfbfc',
      light_border: '#e0e0e4',
      light_header_fill: '#f0f0f2',
      light_header_text: '#27272a',
      light_group_fill: '#e4e4e8',
      light_group_text: '#27272a',
      light_cell_edit_pale: '#ffffff',
      light_cell_edit_deep: '#fafafa',
      light_cell_readonly_pale: '#fafafa',
      light_cell_readonly_deep: '#f4f4f5',
      light_chip: '#f0f0f2',
      light_grid_line: '#e8e8ec',
      dark_background: '#121214',
      dark_border: '#33333a',
      dark_header_fill: '#1e1e21',
      dark_header_text: '#d4d4d8',
      dark_group_fill: '#2a2a2e',
      dark_group_text: '#d4d4d8',
      dark_cell_edit_pale: '#1d1d1f',
      dark_cell_edit_deep: '#1a1a1c',
      dark_cell_readonly_pale: '#252528',
      dark_cell_readonly_deep: '#212124',
      dark_chip: '#1e1e21',
      dark_grid_line: '#2b2b30',
    },
  },
  {
    /**
     * What Classic was until the dark half moved to Grey: indigo-tinted bands
     * in both modes, and exactly the twelve values the old `grid_colors`
     * setting stored. A tenant who had never opened this screen upgrades onto
     * precisely this record, so their unchanged setup shows up here by name
     * rather than as "Custom" — see the test that pins that equality.
     */
    nameKey: 'settings.theme.preset.indigo',
    colors: {
      light_cell_edit_pale: '#FFFFFF',
      light_cell_edit_deep: '#FAF8FC',
      light_cell_readonly_pale: '#FAF8FC',
      light_cell_readonly_deep: '#F5F2F8',
      light_header_fill: '#EDEAF8',
      light_chip: '#EDEAF8',
      dark_cell_edit_pale: '#1B1725',
      dark_cell_edit_deep: '#181422',
      dark_cell_readonly_pale: '#231D30',
      dark_cell_readonly_deep: '#1F1A2B',
      dark_header_fill: '#17162A',
      dark_chip: '#17162A',
    },
  },
  {
    nameKey: 'settings.theme.preset.mint',
    colors: {
      light_topbar: '#0f766e',
      light_topbar_text: '#ecfdf5',
      light_background: '#f7fcf9',
      light_border: '#d3e9dd',
      light_highlight: '#0f766e',
      light_link: '#0f766e',
      light_header_fill: '#eaf8ef',
      light_header_text: '#14532d',
      light_group_fill: '#d9f0e3',
      light_group_text: '#14532d',
      light_cell_edit_pale: '#ffffff',
      light_cell_edit_deep: '#f8fcf9',
      light_cell_readonly_pale: '#f8fcf9',
      light_cell_readonly_deep: '#f1f8f4',
      light_chip: '#eaf8ef',
      light_grid_line: '#e0efe6',
      dark_topbar: '#0b2e26',
      dark_topbar_text: '#d7f5e7',
      dark_background: '#0c1714',
      dark_border: '#25423a',
      dark_highlight: '#5eead4',
      dark_link: '#5eead4',
      dark_header_fill: '#16291d',
      dark_header_text: '#a7e8cf',
      dark_group_fill: '#1c3a2c',
      dark_group_text: '#a7e8cf',
      dark_cell_edit_pale: '#17251c',
      dark_cell_edit_deep: '#142219',
      dark_cell_readonly_pale: '#1d3023',
      dark_cell_readonly_deep: '#1a2b20',
      dark_chip: '#16291d',
      dark_grid_line: '#24382d',
    },
  },
  {
    nameKey: 'settings.theme.preset.peach',
    colors: {
      light_topbar: '#b45309',
      light_topbar_text: '#fff7ed',
      light_background: '#fdfaf7',
      light_border: '#eddfd2',
      light_highlight: '#c2410c',
      light_link: '#c2410c',
      light_header_fill: '#f8f0ea',
      light_header_text: '#7c2d12',
      light_group_fill: '#f2e2d5',
      light_group_text: '#7c2d12',
      light_cell_edit_pale: '#ffffff',
      light_cell_edit_deep: '#fcfaf8',
      light_cell_readonly_pale: '#fcfaf8',
      light_cell_readonly_deep: '#f8f4f1',
      light_chip: '#f8f0ea',
      light_grid_line: '#f0e5db',
      dark_topbar: '#3b2415',
      dark_topbar_text: '#fdeee0',
      dark_background: '#17110c',
      dark_border: '#43301f',
      dark_highlight: '#fb923c',
      dark_link: '#fb923c',
      dark_header_fill: '#291e16',
      dark_header_text: '#f5cfae',
      dark_group_fill: '#3a2a1c',
      dark_group_text: '#f5cfae',
      dark_cell_edit_pale: '#251d17',
      dark_cell_edit_deep: '#221a14',
      dark_cell_readonly_pale: '#30251d',
      dark_cell_readonly_deep: '#2b211a',
      dark_chip: '#291e16',
      dark_grid_line: '#3a2c20',
    },
  },
  {
    nameKey: 'settings.theme.preset.sky',
    colors: {
      light_topbar: '#1d4ed8',
      light_topbar_text: '#eff6ff',
      light_background: '#f8fbfe',
      light_border: '#d5e3f2',
      light_highlight: '#1d4ed8',
      light_link: '#1d4ed8',
      light_header_fill: '#eaf1f8',
      light_header_text: '#1e3a8a',
      light_group_fill: '#d8e6f5',
      light_group_text: '#1e3a8a',
      light_cell_edit_pale: '#ffffff',
      light_cell_edit_deep: '#f8fafc',
      light_cell_readonly_pale: '#f8fafc',
      light_cell_readonly_deep: '#f1f5f8',
      light_chip: '#eaf1f8',
      light_grid_line: '#e2ecf6',
      dark_topbar: '#132a4a',
      dark_topbar_text: '#e0edfd',
      dark_background: '#0b131f',
      dark_border: '#23384f',
      dark_highlight: '#60a5fa',
      dark_link: '#60a5fa',
      dark_header_fill: '#162029',
      dark_header_text: '#b3d3f5',
      dark_group_fill: '#1d3247',
      dark_group_text: '#b3d3f5',
      dark_cell_edit_pale: '#171e25',
      dark_cell_edit_deep: '#141b22',
      dark_cell_readonly_pale: '#1d2730',
      dark_cell_readonly_deep: '#1a222b',
      dark_chip: '#162029',
      dark_grid_line: '#22303d',
    },
  },
  {
    nameKey: 'settings.theme.preset.rose',
    colors: {
      light_topbar: '#9f1239',
      light_topbar_text: '#fff1f2',
      light_background: '#fdf8f9',
      light_border: '#efd8de',
      light_highlight: '#be123c',
      light_link: '#be123c',
      light_header_fill: '#f8eaef',
      light_header_text: '#881337',
      light_group_fill: '#f2d9e1',
      light_group_text: '#881337',
      light_cell_edit_pale: '#ffffff',
      light_cell_edit_deep: '#fcf8f9',
      light_cell_readonly_pale: '#fcf8f9',
      light_cell_readonly_deep: '#f8f1f4',
      light_chip: '#f8eaef',
      light_grid_line: '#f0dee4',
      dark_topbar: '#3b1523',
      dark_topbar_text: '#fde4ec',
      dark_background: '#160d11',
      dark_border: '#42212d',
      dark_highlight: '#f472b6',
      dark_link: '#f472b6',
      dark_header_fill: '#29161d',
      dark_header_text: '#f3c0d3',
      dark_group_fill: '#3a1d29',
      dark_group_text: '#f3c0d3',
      dark_cell_edit_pale: '#25171c',
      dark_cell_edit_deep: '#221419',
      dark_cell_readonly_pale: '#301d23',
      dark_cell_readonly_deep: '#2b1a20',
      dark_chip: '#29161d',
      dark_grid_line: '#391f29',
    },
  },
  {
    nameKey: 'settings.theme.preset.grey',
    colors: {
      light_topbar: '#3f3f46',
      light_topbar_text: '#fafafa',
      light_background: '#fbfbfc',
      light_border: '#e0e0e4',
      light_highlight: '#3f3f46',
      light_link: '#3f3f46',
      light_header_fill: '#f0f0f2',
      light_header_text: '#27272a',
      light_group_fill: '#e4e4e8',
      light_group_text: '#27272a',
      light_cell_edit_pale: '#ffffff',
      light_cell_edit_deep: '#fafafa',
      light_cell_readonly_pale: '#fafafa',
      light_cell_readonly_deep: '#f4f4f5',
      light_chip: '#f0f0f2',
      light_grid_line: '#e8e8ec',
      dark_topbar: '#232326',
      dark_topbar_text: '#f4f4f5',
      dark_background: '#121214',
      dark_border: '#33333a',
      dark_highlight: '#d4d4d8',
      dark_link: '#d4d4d8',
      dark_header_fill: '#1e1e21',
      dark_header_text: '#d4d4d8',
      dark_group_fill: '#2a2a2e',
      dark_group_text: '#d4d4d8',
      dark_cell_edit_pale: '#1d1d1f',
      dark_cell_edit_deep: '#1a1a1c',
      dark_cell_readonly_pale: '#252528',
      dark_cell_readonly_deep: '#212124',
      dark_chip: '#1e1e21',
      dark_grid_line: '#2b2b30',
    },
  },
];

/* ------------------------------------------------------------ defaults --- */

/**
 * What each role looks like when nothing is stored — a mirror of the Numtable
 * values in `src/styles/tokens.css`, resolved through the palette.
 *
 * It exists only so the settings screen can show BOTH modes at once: the live
 * value of a token can be read with `getComputedStyle`, but only for the theme
 * currently painted, and the screen edits light and dark side by side. Nothing
 * outside the picker's swatch depends on this table, and an unset role still
 * resolves from the real tokens at runtime — including the Procalendar accent,
 * which deliberately differs from the value listed here.
 */
export const THEME_DEFAULTS: Readonly<Record<string, string>> = {
  light_topbar: '#4d46c5',
  light_topbar_text: '#ffffff',
  light_background: '#fbfaff',
  light_border: '#ded9ee',
  light_highlight: '#5a55ec',
  light_text: '#0f172a',
  light_muted: '#5a6879',
  light_link: '#5a55ec',
  light_sheet_name_text: '#0f172a',
  light_header_fill: '#edeaf8',
  light_header_text: '#5b2d8c',
  light_group_fill: '#edeaf8',
  light_group_text: '#5b2d8c',
  light_cell_edit_pale: '#ffffff',
  light_cell_edit_deep: '#faf8fc',
  light_cell_readonly_pale: '#faf8fc',
  light_cell_readonly_deep: '#f5f2f8',
  light_chip: '#edeaf8',
  light_grid_line: '#eae6f0',
  light_success: '#15803d',
  light_danger: '#b91c1c',
  light_warning: '#b45309',
  dark_topbar: '#211c38',
  dark_topbar_text: '#f6f3ff',
  dark_background: '#101020',
  dark_border: '#342d4e',
  dark_highlight: '#a5a0ff',
  dark_text: '#e2e8f0',
  dark_muted: '#94a3b8',
  dark_link: '#a5a0ff',
  dark_sheet_name_text: '#e2e8f0',
  dark_header_fill: '#17162a',
  dark_header_text: '#c9b8e4',
  dark_group_fill: '#17162a',
  dark_group_text: '#c9b8e4',
  dark_cell_edit_pale: '#1b1725',
  dark_cell_edit_deep: '#181422',
  dark_cell_readonly_pale: '#231d30',
  dark_cell_readonly_deep: '#1f1a2b',
  dark_chip: '#17162a',
  dark_grid_line: '#2e2740',
  dark_success: '#4ade80',
  dark_danger: '#f87171',
  dark_warning: '#fbbf24',
};

/** The swatch to show for a role the tenant has not chosen. */
export function defaultFor(mode: ThemeColorMode, base: string): string {
  return THEME_DEFAULTS[themeKey(mode, base)] ?? '#000000';
}

/** The value to show in the picker: the tenant's choice, else the default. */
export function effectiveColor(
  colors: ThemeColors,
  mode: ThemeColorMode,
  base: string,
): string {
  const chosen = colors[themeKey(mode, base)];
  return isHexColor(chosen) ? chosen : defaultFor(mode, base);
}
